import { lazy } from 'react';
import { Button } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { DealViewExtensions } from '@ayocore/erp/crm/client/components/Deals/views/DealViewExtensions';
import { CRM_DEAL_STATUSES, CRM_DEAL_TYPES } from '@ayocore/erp/crm/constants';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { CheckDealStatus } from '../components/CheckDealStatus';

const GrasiasDealRowsPanel = lazy(() => import('../components/GrasiasDealRowsPanel'));
const GrasiasDealExpenses = lazy(() =>
  import('../components/GrasiasDealExpenses').then(m => ({ default: m.GrasiasDealExpenses }))
);
const DealDocuments = lazy(() =>
  import('@ayocore/erp/crm/client/components/Deals/components/DealDocuments').then(m => ({
    default: m.DealDocuments,
  }))
);

/**
 * Регистрирует расширения grasias для DealView.
 * - Заменяет вкладку «Позиции» на «Товары и услуги» только для сделок типа SALE.
 * - Заменяет вкладку «Расходы» на Grasias-версию (IS_OUR_COMPANY скрытие сумм).
 * - Добавляет вкладку «Документы» (привязка заказов 1С к сделке).
 *
 * Вызывается один раз из bootstrap.ts.
 */
/** Кнопка «Проверить статус» в заголовке сделки — открывает диагностику + пересчёт. */
function CheckDealStatusButton({ dealId, onReload }: { dealId: number; onReload: () => void }) {
  const modal = useModal(CheckDealStatus);
  return (
    <Button
      size='xs'
      variant='light'
      leftSection={<IconRefresh size={14} />}
      onClick={() =>
        modal.open(
          { dealId, onReload, title: 'Проверка статуса сделки' },
          { size: 'md', centered: true }
        )
      }
    >
      Проверить статус
    </Button>
  );
}

export function registerGrasiasDealViewExtensions(): void {
  // Кнопка диагностики статуса — только для сделок типа SALE (логика статусов SALE_*).
  DealViewExtensions.headerActions.register(({ deal, reload }) => {
    if (deal.deal_type?.slug !== CRM_DEAL_TYPES.SALE) return null;
    return <CheckDealStatusButton dealId={deal.id} onReload={reload} />;
  });

  DealViewExtensions.tabs.register(({ deal, reload }) => {
    if (deal.deal_type?.slug !== CRM_DEAL_TYPES.SALE) return null;

    return {
      value: 'grasias-rows',
      label: 'Товары и услуги',
      overrides: 'rows',
      content: <GrasiasDealRowsPanel deal={deal} onReload={reload} />,
    };
  });

  DealViewExtensions.tabs.register(({ deal, reload }) => {
    if (deal.deal_type?.slug !== CRM_DEAL_TYPES.SALE) return null;

    return {
      value: 'grasias-expenses',
      label: 'Расходы',
      overrides: 'expenses',
      content: <GrasiasDealExpenses deal={deal} onReload={reload} />,
    };
  });

  DealViewExtensions.tabs.register(({ deal }) => ({
    value: 'grasias-documents',
    label: 'Документы',
    overrides: 'documents',
    content: (
      <DealDocuments
        dealId={deal.id}
        partnerId={deal.partner?.id}
        dealAmount={
          deal.rows_total != null
            ? Number(deal.rows_total)
            : deal.amount != null
              ? Number(deal.amount)
              : null
        }
        readOnly={deal.archived || deal.deal_status?.slug === CRM_DEAL_STATUSES.SALE_CLOSED}
      />
    ),
  }));
}
